const formatNumber = (value, opts = {}) => {
  const num = Number(value || 0)
  return num.toLocaleString('en-US', {
    minimumFractionDigits: opts.min ?? 0,
    maximumFractionDigits: opts.max ?? 0
  })
}

const formatCurrency = (value, opts = {}) => {
  const num = Number(value || 0)
  const decimals = opts.decimals ?? 0
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

const safeGet = (obj, path, fallback = 0) => {
  try {
    return path.split('.').reduce((acc, k) => acc?.[k], obj) ?? fallback
  } catch (err) {
    return fallback
  }
}

/*
  graphStatistics[tab] = {
    Dollars: { sum, avg },
    SP: { sum, avg },
    Units: { sum, avg },
    TicketCount: { sum, avg },
    DollarsPerSP: { avg },
    DollarsPerUnit: { avg },
    UnitsPerItem: { avg },
    Items: { sum }
  }
*/

const buildDashboardUnitsConfig = ({ catNames, catOptions, billableMode = 'all' }) => {
  const unit1 = (tab) => catOptions?.[tab]?.tabLabels?.unitSign1 || ''
  const unit2 = (tab) => catOptions?.[tab]?.tabLabels?.unitSign2 || ''

  const showRevenueLost = billableMode === 'nonbillable'

  const aiSpMonthlyLines = [
    { key: 'Dollars.sum', label: () => 'Total revenue:', format: (v) => formatCurrency(v, { decimals: 0 }) },
    { key: 'EffectiveDollarsPerSP', label: () => 'Effective $/SP:', format: (v) => formatCurrency(v, { decimals: 2 }), valueColor: 'success.main' },
    { key: 'BillableUnits.sum', label: () => 'Billable SP:', format: (v) => formatNumber(v), tooltipKey: 'UnitsPercentages' },
    { key: 'NonBillableUnits.sum', label: () => 'Non-billable SP:', format: (v) => formatNumber(v) },
    ...(showRevenueLost
      ? [
          { key: 'Deficit.sum', label: () => 'Revenue lost (non-billable):', format: (v) => formatCurrency(v, { decimals: 2 }), valueColor: 'error.main' },
          { key: 'Deficit.avg', label: () => 'Avg. deficit per month:', format: (v) => formatCurrency(v, { decimals: 2 }), valueColor: 'error.main' }
          // { key: 'DeficitDollarsPerSP', label: () => 'Deficit $/SP:', format: (v) => formatCurrency(v, { decimals: 2 }), valueColor: 'error.main' }
        ]
      : [{ key: 'DollarsPerUnit.avgPerMonth', label: () => 'Avg. $/SP per month:', format: (v) => formatCurrency(v, { decimals: 2 }) }])
  ]

  return {
    [catNames.SP_MONTHLY]: {
      lines: [
        { key: 'Dollars.sum', label: (t) => `Total ${unit1(t)}:`, format: (v) => formatCurrency(v, { decimals: 0 }) },
        { key: 'SP.sum', label: (t) => `Total ${unit2(t)}:`, format: (v) => formatNumber(v) },
        { key: 'DollarsPerSP.avg', label: (t) => `Avg. Total ${unit1(t)}${unit2(t)}:`, format: (v) => formatCurrency(v, { decimals: 2 }) }
      ],
      trailing: { key: 'DollarsPerSP.sum', label: (t) => `Total ${unit1(t)}${unit2(t)}:`, format: (v) => formatCurrency(v, { decimals: 2 }) }
    },

    [catNames.AI_SP_MONTHLY]: {
      lines: aiSpMonthlyLines,
      ...(showRevenueLost
        ? {
            trailing: {
              key: 'DollarsPerUnit.sum',
              label: () => 'Deficit (total):',
              format: (v) => `${Number(v || 0).toFixed(2)}%`,
              valueColor: 'error.main'
            }
          }
        : {})
    },

    [catNames.BILLING]: {
      lines: [
        { key: 'Dollars.avg', label: (t) => `Avg. Total ${unit1(t)}/Month:`, format: (v) => formatCurrency(v, { decimals: 0 }) }
      ],
      trailing: { key: 'Dollars.sum', label: (t) => `Total ${unit1(t)}:`, format: (v) => formatCurrency(v, { decimals: 0 }) }
    },

    [catNames.AI_BILLING]: {
      lines: [
        { key: 'Dollars.avg', label: () => 'Avg. QBO $/Month:', format: (v) => formatCurrency(v, { decimals: 0 }) },
        { key: 'Dollars.sum', label: () => 'Total QBO $:', format: (v) => formatCurrency(v, { decimals: 0 }) },
        { key: 'ChargeCount', label: () => '# of charges:', format: (v) => formatNumber(v) }
      ]
    },

    [catNames.TRACKING]: {
      lines: [
        { key: 'SP.avg', label: (t) => `Avg. Total ${unit2(t)}/Month:`, format: (v) => formatNumber(v) }
      ],
      trailing: { key: 'SP.sum', label: (t) => `Total ${unit2(t)}:`, format: (v) => formatNumber(v) }
    },

    [catNames.TICKETS_MONTHLY]: {
      lines: [
        { key: 'TicketCount.avg', label: () => 'Avg. Total Tickets/Month:', format: (v) => formatNumber(v) }
      ],
      trailing: { key: 'TicketCount.sum', label: () => 'Total Tickets:', format: (v) => formatNumber(v) }
    },

    [catNames.AI_TRACKING]: {
      lines: [
        { key: 'Units.avg', label: () => 'Avg. Total SP/Month:', format: (v) => formatNumber(v) }
      ],
      trailing: { key: 'Units.sum', label: () => 'Total SP:', format: (v) => formatNumber(v), tooltipKey: 'UnitsPercentages' }
    },

    [catNames.AI_TICKET_SP]: {
      lines: [
        { key: 'UnitsPerItem.avg', label: () => 'Avg. SP/ZD ticket (month):', format: (v) => formatNumber(v, { min: 2, max: 2 }) },
        { key: 'Items.avg', label: () => 'Avg. ZD ticket/month:', format: (v) => formatNumber(v, { min: 2, max: 2 }) }
      ],
      trailing: { key: 'Items.sum', label: () => 'Total ZD tickets:', format: (v) => formatNumber(v) }
    }
  }
}

const getTabStats = (graphStatistics, activeTab) => {
  if (!graphStatistics || typeof graphStatistics !== 'object') return {}
  if (!activeTab) return {}
  return graphStatistics[activeTab] || {}
}

const readStatValue = (tabStats, key) => {
  return safeGet(tabStats, key, 0)
}

export {
  buildDashboardUnitsConfig,
  getTabStats,
  readStatValue,
  formatNumber,
  formatCurrency
}
